import type { ReactNode } from "react";

import { cn } from "../../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";

export type DataTableProgressiveListCellItem = {
  id?: string;
  label: ReactNode;
  detail?: ReactNode;
  icon?: ReactNode;
};

export type DataTableProgressiveListCellProps = {
  items?: DataTableProgressiveListCellItem[] | null;
  emptyText?: ReactNode;
  title?: ReactNode;
  maxVisible?: number;
  className?: string;
  triggerClassName?: string;
  visibleItemClassName?: string;
  overflowClassName?: string;
  contentClassName?: string;
  popoverClassName?: string;
  overflowLabel?: (count: number) => ReactNode;
  renderVisibleItem?: (item: DataTableProgressiveListCellItem, index: number) => ReactNode;
  renderPopoverItem?: (item: DataTableProgressiveListCellItem, index: number) => ReactNode;
};

function getItemKey(item: DataTableProgressiveListCellItem, index: number) {
  if (item.id) return item.id;
  if (typeof item.label === "string") return item.label;
  return index;
}

export function DataTableProgressiveListCell({
  items,
  emptyText = "—",
  title,
  maxVisible = 1,
  className,
  triggerClassName,
  visibleItemClassName,
  overflowClassName,
  contentClassName,
  popoverClassName,
  overflowLabel = (count) => `+${count}`,
  renderVisibleItem,
  renderPopoverItem,
}: DataTableProgressiveListCellProps) {
  const normalizedItems = items?.filter(Boolean) ?? [];
  const visibleItems = normalizedItems.slice(0, maxVisible);
  const hiddenCount = Math.max(normalizedItems.length - visibleItems.length, 0);
  const hasOverflow = hiddenCount > 0;

  if (!normalizedItems.length) {
    return <span className='text-muted-foreground'>{emptyText}</span>;
  }

  const visibleContent = (
    <span className={cn("inline-flex max-w-full min-w-0 items-center gap-1.5", contentClassName)}>
      {visibleItems.map((item, index) => (
        <span
          key={getItemKey(item, index)}
          className={cn("inline-flex min-w-0 items-center gap-1.5", visibleItemClassName)}
        >
          {renderVisibleItem ? (
            renderVisibleItem(item, index)
          ) : (
            <>
              {item.icon ? <span className='shrink-0'>{item.icon}</span> : null}
              <span className='min-w-0 truncate'>{item.label}</span>
            </>
          )}
        </span>
      ))}
      {hasOverflow ? (
        <span
          className={cn(
            "inline-flex h-5 shrink-0 items-center rounded-full border border-border/80 bg-background px-1.5 text-[0.6875rem] leading-none font-medium text-muted-foreground tabular-nums",
            overflowClassName,
          )}
        >
          {overflowLabel(hiddenCount)}
        </span>
      ) : null}
    </span>
  );

  if (!hasOverflow) {
    return <span className={cn("inline-flex max-w-full min-w-0 font-medium", className)}>{visibleContent}</span>;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type='button'
          className={cn(
            "group/progressive inline-flex max-w-full min-w-0 items-center text-left font-medium outline-none transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            triggerClassName,
            className,
          )}
          aria-label={typeof title === "string" ? title : undefined}
        >
          {visibleContent}
        </button>
      </PopoverTrigger>
      <PopoverContent align='start' sideOffset={4} className={cn("w-72 p-2.5", popoverClassName)}>
        {title ? (
          <p className='mb-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase'>{title}</p>
        ) : null}
        <ul className='space-y-1.5'>
          {normalizedItems.map((item, index) => (
            <li key={getItemKey(item, index)}>
              {renderPopoverItem ? (
                renderPopoverItem(item, index)
              ) : (
                <div className='flex min-h-6 items-baseline justify-between gap-3 text-sm'>
                  <span className='min-w-0 truncate font-medium'>{item.label}</span>
                  {item.detail ? (
                    <span className='shrink-0 text-xs tabular-nums text-muted-foreground'>{item.detail}</span>
                  ) : null}
                </div>
              )}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
